import { supabase } from '@/lib/supabase/client'
import { uploadDocument } from '@/services/document-upload.service'
import {
  STMIDBEquipmentService,
  getSTMIDBUploadCode,
  mapSTMIDBToWLLCategoryCode,
  mapWLLDocumentTypeToSTMIDB,
  resolveIsSTMIDBEquipment,
} from '@/services/api/stmidbEquipmentService'
import { isSlingCategory, matchesCategoryFamily } from '@/utils/categoryFamily'

/**
 * Category code for NDT filtering of SLB equipment. STMIDB's equipment_type is
 * often wrong on sub-equipment (R1 slings typed "Offshore Service Container"),
 * and mapSTMIDBToWLLCategoryCode prefers the type, so map STMIDB's own
 * category alone first (SDLA -> R10) and only use the type when it is empty.
 */
export function getSTMIDBNdtCategoryCode(
  category: string | null,
  equipmentType: string | null,
): string | null {
  const raw = (category || '').trim().toUpperCase()
  if (!raw) return mapSTMIDBToWLLCategoryCode(category, equipmentType)
  return mapSTMIDBToWLLCategoryCode(raw, null) || raw
}

const NDT_DATE_TEMPLATE_ID = 'd0dd8453-662a-49af-8571-0d8a5a1deb20'

const addYearsToIsoDate = (isoDate: string, years: number) => {
  const [year, month, day] = isoDate.split('-').map(Number)
  return `${year + years}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

export async function updateEquipmentNdtDates(
  equipmentId: string,
  testDate: string,
  ndtIntervalYears = 4,
): Promise<void> {
  if (await resolveIsSTMIDBEquipment(equipmentId)) {
    // set_stmidb_ndt_date also sets STMIDB's next_ndt_date (48M DUE).
    await STMIDBEquipmentService.setNDTDate(equipmentId, testDate)
    return
  }

  const nextNdt = addYearsToIsoDate(testDate, ndtIntervalYears)

  const { error: colError } = await supabase
    .from('equipment_v2')
    .update({
      last_ndt_date: testDate,
      next_ndt_date: nextNdt,
      updated_at: new Date().toISOString(),
    })
    .eq('id', equipmentId)

  if (colError) {
    throw new Error('Rapport lagret, men NDT-datoer kunne ikke oppdateres')
  }

  const { data: existing, error: specFetchError } = await supabase
    .from('equipment_specifications_v2')
    .select('id')
    .eq('equipment_id', equipmentId)
    .in('spec_key', ['ndt_date', 'last_ndt_date'])

  if (specFetchError) {
    throw new Error('Rapport lagret, men NDT-datoer kunne ikke oppdateres')
  }

  if (existing && existing.length > 0) {
    const { error: specUpdateError } = await supabase
      .from('equipment_specifications_v2')
      .update({
        value_date: testDate,
        value_text: null,
        value_numeric: null,
        value_integer: null,
        value_boolean: null,
        value_reference_id: null,
      })
      .in('id', existing.map((s) => s.id))

    if (specUpdateError) {
      throw new Error('Rapport lagret, men NDT-datoer kunne ikke oppdateres')
    }
    return
  }

  const { data: { user } } = await supabase.auth.getUser()
  const { error: specInsertError } = await supabase
    .from('equipment_specifications_v2')
    .insert({
      equipment_id: equipmentId,
      template_id: NDT_DATE_TEMPLATE_ID,
      spec_key: 'ndt_date',
      value_date: testDate,
      validation_status: 'pending',
      is_validated: false,
      version: 1,
      created_by: user?.id,
    })

  if (specInsertError) {
    throw new Error('Rapport lagret, men NDT-datoer kunne ikke oppdateres')
  }
}

export type NdtReportStatus = 'Accepted' | 'Not Accepted'

export async function updateEquipmentStatusFromNdt(
  equipmentId: string,
  reportStatus: NdtReportStatus,
): Promise<void> {
  const inspectionStatus = reportStatus === 'Not Accepted' ? 'NC' : 'OK'

  if (await resolveIsSTMIDBEquipment(equipmentId)) {
    // STMIDB stores the inspection code in `status` (read back as equipment_status).
    await STMIDBEquipmentService.updateEquipment(equipmentId, { status: inspectionStatus })
    return
  }

  const { error } = await supabase
    .from('equipment_v2')
    .update({ inspection_status: inspectionStatus, updated_at: new Date().toISOString() })
    .eq('id', equipmentId)

  if (error) {
    throw new Error('Rapport lagret, men utstyrsstatus kunne ikke oppdateres')
  }
}

export const applySelectedEquipmentToForm = <T extends { objectDescription: string; objects: string[] }>(
  prev: T,
  equipment: { name: string; serial_number: string | null },
): T => ({
  ...prev,
  objectDescription: equipment.name || prev.objectDescription,
  objects: [equipment.serial_number || prev.objects[0] || ''],
})

export type AttachedSling = {
  equipmentId: string
  serial: string
  isChain: boolean
}

/** SLB counterpart of fetchAttachedR1R3Slings: STMIDB children via parent_equipment_id. */
export async function fetchAttachedSTMIDBR1R3Slings(mainEquipmentId: string): Promise<AttachedSling[]> {
  const children = await STMIDBEquipmentService.getSubEquipment(mainEquipmentId)
  const slings: AttachedSling[] = []
  for (const child of children) {
    const code = (getSTMIDBNdtCategoryCode(child.category, child.equipment_type) || '').toUpperCase()
    if (!isSlingCategory(code)) continue
    const serial = child.serial_number || child.asset_number
    if (!serial) continue
    slings.push({ equipmentId: child.id, serial, isChain: matchesCategoryFamily(code, 'R1') })
  }
  return slings
}

export async function fetchAttachedR1R3Slings(mainEquipmentId: string): Promise<AttachedSling[]> {
  if (await resolveIsSTMIDBEquipment(mainEquipmentId)) {
    return fetchAttachedSTMIDBR1R3Slings(mainEquipmentId)
  }

  const { data } = await supabase
    .from('equipment_relationship_v2')
    .select(`
      sub_equipment:equipment_v2!equipment_relationship_v2_sub_equipment_id_fkey(
        id,
        serial_number,
        asset_number,
        categories_v2(code)
      )
    `)
    .eq('main_equipment_id', mainEquipmentId)

  const slings: AttachedSling[] = []
  for (const rel of data || []) {
    const sub = (Array.isArray(rel.sub_equipment) ? rel.sub_equipment[0] : rel.sub_equipment) as {
      id: string
      serial_number: string | null
      asset_number: string | null
      categories_v2: { code: string } | { code: string }[] | null
    } | null
    if (!sub) continue

    const category = Array.isArray(sub.categories_v2) ? sub.categories_v2[0] : sub.categories_v2
    const code = (category?.code || '').toUpperCase()
    if (!isSlingCategory(code)) continue

    const serial = sub.serial_number || sub.asset_number
    if (!serial) continue
    slings.push({ equipmentId: sub.id, serial, isChain: matchesCategoryFamily(code, 'R1') })
  }
  return slings
}

export async function attachNdtReportToEquipment({
  pdfBlob,
  fileName,
  customerName,
  serialNumber,
  equipmentId,
  testDate,
  reportStatus,
  ndtIntervalYears = 4,
  updateNdtDates = true,
}: {
  pdfBlob: Blob
  fileName: string
  customerName: string
  serialNumber: string
  equipmentId: string
  testDate: string
  reportStatus: NdtReportStatus
  ndtIntervalYears?: number
  updateNdtDates?: boolean
}): Promise<void> {
  const equipmentDocFile = new File([pdfBlob], fileName, { type: 'application/pdf' })

  // equipment_documents has a foreign key to equipment_v2, so SLB reports go
  // to STMIDB storage instead.
  if (await resolveIsSTMIDBEquipment(equipmentId)) {
    const equipment = await STMIDBEquipmentService.getEquipmentById(equipmentId)
    await STMIDBEquipmentService.uploadDocument({
      equipmentId,
      equipmentCode: getSTMIDBUploadCode({
        id: equipmentId,
        owner_id: equipment?.equipment_code,
        asset_number: equipment?.asset_number,
        serial_number: equipment?.serial_number,
      }),
      documentType: mapWLLDocumentTypeToSTMIDB('ndt_report'),
      file: equipmentDocFile,
    })
    if (updateNdtDates) {
      await STMIDBEquipmentService.setNDTDate(equipmentId, testDate)
    }
    await updateEquipmentStatusFromNdt(equipmentId, reportStatus)
    return
  }

  const equipmentUpload = await uploadDocument({
    file: equipmentDocFile,
    customerName: customerName || 'unknown',
    serialNumber,
    equipmentId,
    documentType: 'ndt_report',
  })
  if (!equipmentUpload.success) {
    throw new Error(equipmentUpload.error || 'Kunne ikke lagre rapport på utstyr')
  }

  if (updateNdtDates) {
    await updateEquipmentNdtDates(equipmentId, testDate, ndtIntervalYears)
  }
  await updateEquipmentStatusFromNdt(equipmentId, reportStatus)
}
